import { SocialNetwork } from './social';
import { ContentCategory } from './brand';
import { DynamicTextType } from './template';
import { PublishStatus } from './scheduling';

// Image source types for posts
export type ImageSource = 'stock' | 'pastel' | 'asset' | 'ai';

// AI model/provider types for tracking which AI generated the image
export type AIModel = 'kie' | 'cloudflare-sd' | null;

// Kie.ai model IDs available for Business users
export type KieModel = 'z-image' | 'nano-banana-pro' | 'flux-2/pro-text-to-image';

export const KIE_MODELS: { id: KieModel; name: string }[] = [
  { id: 'z-image', name: 'Z-Image' },
  { id: 'nano-banana-pro', name: 'Nano Banana Pro' },
  { id: 'flux-2/pro-text-to-image', name: 'Flux 2 Pro' },
];

// Pre-generated dynamic text variations for templates
export interface DynamicTexts {
  headline: string;
  tagline: string;
  cta: string;
  quote: string;
  stat?: string;
}

export interface GeneratedPost {
  id: string;
  network: SocialNetwork;
  content: string;
  imageUrl: string | null;
  // Image prompts for different generation methods
  stockImagePrompt: string | null;  // Prompt used to search stock images
  aiImagePrompt: string | null;     // Prompt for AI image generation
  imageSource: ImageSource;         // Where the image came from
  // AI model used for image generation (only set when imageSource is 'ai')
  aiModel?: AIModel;
  // Pending AI image task ID (KIE) - when using lazy load
  pendingImageTaskId?: string | null;
  // Legacy field for backwards compatibility
  imagePrompt?: string | null;
  contentCategory: ContentCategory | null;
  // Pre-generated dynamic text for templates
  dynamicTexts?: DynamicTexts;
  // Background image pan offset (percentage from center, -100 to 100)
  backgroundOffsetX?: number;
  backgroundOffsetY?: number;
  // Background image scale (1.0 = default cover fit, >1 = zoom in, <1 = zoom out)
  backgroundScale?: number;
  // Specific AI model used (e.g., 'SeedDream 3.0', 'Nano Banana')
  aiModelDetail?: string | null;
  // AI fallback indicator - true when AI generation failed and fell back to stock
  aiFallback?: boolean;
  // Scheduling & publishing fields
  scheduledFor?: string | null;
  publishedAt?: string | null;
  publishStatus?: PublishStatus;
  publishError?: string | null;
  externalPostId?: string | null;
  createdAt: string;
}

// Request body for updating post properties
export interface UpdatePostRequest {
  backgroundOffsetX?: number;
  backgroundOffsetY?: number;
  backgroundScale?: number;
  content?: string;
}

// Helper to get dynamic text by type
export function getDynamicText(post: GeneratedPost, type: DynamicTextType): string {
  if (!post.dynamicTexts) return '';
  if (type === 'custom') return post.dynamicTexts.headline; // Fallback for custom
  if (type === 'stat') return post.dynamicTexts.stat || '';
  return post.dynamicTexts[type] || '';
}

// Per-platform post counts
export interface PlatformPostCounts {
  instagram?: number;
  facebook?: number;
  twitter?: number;
  linkedin?: number;
  tiktok?: number;
  threads?: number;
  gbp?: number;
  bluesky?: number;
  youtube?: number;
  pinterest?: number;
  telegram?: number;
}

// Configuration for image sources during generation
export interface ImageSourceConfig {
  enabledSources: ImageSource[];
  mix: { stock: number; pastel: number; asset: number; ai: number };
  selectedAssetIds: string[];
  // Custom colors for 'pastel' (colored) backgrounds - cycles through in order
  customColors?: string[];
  // Kie.ai model to use for AI images ('mix' = equal split across all models)
  kieModel?: string;
}

export interface GenerateRequest {
  // Legacy: single count for all networks
  networks?: SocialNetwork[];
  count?: number;
  // New: per-platform counts
  platformCounts?: PlatformPostCounts;
  customPrompt?: string;
  // Image source configuration
  imageSourceConfig?: ImageSourceConfig;
}

export interface ExportRow {
  platform: SocialNetwork;
  contentCategory: string;
  content: string;
  stockImagePrompt: string;
  aiImagePrompt: string;
  imageSource: ImageSource;
  imageUrl: string;
}
